import { type TFunction } from "i18next";
import type { ConsentGovernanceItemWrite } from "src/api/client-api/consent/useManageConsentGovernanceAdmin";
import { type Locale } from "src/api/locales/useLocales";
import { CONSENT_CATEGORY_CONFIG, CONSENT_CREATION_CATEGORY_SPLITS } from "./consentCategoryConfig";

export type ConsentCreationType = "document" | "opt_in";
export type ConsentDraftLabel = {
    description: string;
    name: string;
};

export type ConsentDraftVersion = {
    content: string;
    documentUrl: string;
};

export type ConsentCreationState = {
    category: string;
    code: string;
    labelDrafts: Record<string, ConsentDraftLabel>;
    lastTouchedLocale: string;
    type: ConsentCreationType;
    version: string;
    versionDrafts: Record<string, ConsentDraftVersion>;
    versionNote: string;
};

export type ConsentCreationStepErrors = {
    category: string;
    code: string;
    content: string;
    description: string;
    documentUrl: string;
    name: string;
    version: string;
};

export const CONSENT_CREATION_STEP_KEYS = [
    "settings.consent.document_wizard.steps.type_category",
    "settings.consent.document_wizard.steps.labels",
    "settings.consent.document_wizard.steps.initial_version",
    "settings.consent.document_wizard.steps.summary",
] as const;

export const TECHNICAL_CODE_PATTERN = /^[a-z0-9_]+$/;

export const buildEmptyLabelDrafts = (locales: Locale[]) =>
    Object.fromEntries(locales.map((locale) => [locale.code ?? "", { description: "", name: "" }]));

export const buildEmptyVersionDrafts = (locales: Locale[]) =>
    Object.fromEntries(locales.map((locale) => [locale.code ?? "", { content: "", documentUrl: "" }]));

export const buildInitialConsentCreationState = (
    locales: Locale[],
    defaultLocale: string,
    initialType: ConsentCreationType,
): ConsentCreationState => ({
    category: "",
    code: "",
    labelDrafts: buildEmptyLabelDrafts(locales),
    lastTouchedLocale: defaultLocale,
    type: initialType,
    version: "1.0",
    versionDrafts: buildEmptyVersionDrafts(locales),
    versionNote: "Initial version",
});

export const normalizeOptionalString = (value: string) => {
    const normalized = value.trim();

    return normalized.length > 0 ? normalized : null;
};

export const buildGeneratedTitle = (name: string) => name.trim();

export const buildConsentCreationItemPayload = (
    state: Pick<ConsentCreationState, "category" | "code" | "type">,
    labelDraft: Pick<ConsentDraftLabel, "description" | "name">,
): ConsentGovernanceItemWrite => ({
    code: state.code.trim(),
    type: state.type,
    name: labelDraft.name.trim(),
    description: normalizeOptionalString(labelDraft.description),
    required: state.type === "document",
    active: true,
    sortOrder: 0,
    category: state.category || null,
});

export const getAvailableCategoryKeys = (type: ConsentCreationType) => {
    const directCategories = CONSENT_CREATION_CATEGORY_SPLITS[type];

    if (directCategories) {
        return [...directCategories];
    }

    return Object.keys(CONSENT_CATEGORY_CONFIG);
};

export const isCategoryCompatibleWithType = (type: ConsentCreationType, category: string) =>
    getAvailableCategoryKeys(type).includes(category as never);

export const getCategoryDescription = (category: string, t: TFunction) => {
    switch (category) {
        case "cgu":
            return t(
                "settings.consent.document_wizard.category.terms_description",
                "Terms imposed on users of your platform.",
            );
        case "platform_policy":
            return t(
                "settings.consent.document_wizard.category.platform_policy_description",
                "Policy complementary to the terms.",
            );
        case "product_comms":
            return t(
                "settings.consent.document_wizard.category.product_comms_description",
                "Product news, updates, and platform changelog.",
            );
        case "institutional_comms":
            return t(
                "settings.consent.document_wizard.category.institutional_comms_description",
                "Newsletter, news, and information from your organization.",
            );
        case "data_sharing":
            return t(
                "settings.consent.document_wizard.category.data_sharing_description",
                "Aggregated or anonymized data sharing with the owning organization.",
            );
        case "events":
            return t(
                "settings.consent.document_wizard.category.events_description",
                "Invitations to events, webinars, or training sessions.",
            );
        case "surveys":
            return t(
                "settings.consent.document_wizard.category.surveys_description",
                "Surveys, product feedback, and user research.",
            );
        default:
            return "";
    }
};

export const getTypeLabel = (type: ConsentCreationType, t: TFunction) =>
    type === "document" ?
        t("settings.consent.document_wizard.document_type", "Contract document")
    :   t("settings.consent.document_wizard.preference_type", "Optional preference");

export const getTypeHint = (type: ConsentCreationType, t: TFunction) =>
    type === "document" ?
        t(
            "settings.consent.document_wizard.document_type_hint",
            "Blocking, versioned acceptance shown in the consent gate.",
        )
    :   t(
            "settings.consent.document_wizard.preference_type_hint",
            "The user chooses freely. Available in the preference center.",
        );

export const getLabelsInfo = (type: ConsentCreationType, t: TFunction) =>
    type === "document" ?
        t(
            "settings.consent.document_wizard.labels_info_document",
            "The label is shown in the consent gate and preferences. The description is internal-only and not displayed to users. The app default language is required.",
        )
    :   t(
            "settings.consent.document_wizard.labels_info_preference",
            "The label is shown in the preference center. The description is internal-only and not displayed to users. The app default language is required.",
        );

export const getVersionInfo = (type: ConsentCreationType, t: TFunction) =>
    type === "document" ?
        t(
            "settings.consent.document_wizard.version_info_document",
            "The initial version contains the legal text shown to the user during consent. It is published immediately on creation.",
        )
    :   t(
            "settings.consent.document_wizard.version_info_preference",
            "The initial version contains the preference text shown in the preference center. It is published immediately on creation.",
        );

export const getContentLabel = (type: ConsentCreationType, t: TFunction) =>
    type === "document" ?
        t("settings.consent.document_wizard.content_label_document", "Legal content")
    :   t("settings.consent.document_wizard.content_label_preference", "Preference text");

export const getContentPlaceholder = (type: ConsentCreationType, t: TFunction) =>
    type === "document" ?
        t(
            "settings.consent.document_wizard.content_placeholder_document",
            "Write the legal text shown to the user here...",
        )
    :   t(
            "settings.consent.document_wizard.content_placeholder_preference",
            "Clearly explain the preference available to the user...",
        );

export const getSummaryReadyMessage = (type: ConsentCreationType, t: TFunction) =>
    type === "document" ?
        t(
            "settings.consent.document_wizard.summary_ready_document",
            "Everything is ready. Review the information before creating the purpose. It will become active on your platform immediately.",
        )
    :   t(
            "settings.consent.document_wizard.summary_ready_preference",
            "Everything is ready. Review the information before creating the purpose. It will become available in the preference center immediately.",
        );

export const getSummaryWarning = (type: ConsentCreationType, t: TFunction) =>
    type === "document" ?
        t(
            "settings.consent.document_wizard.warning_document",
            "This contract document will be shown to users as a blocking gate at their next login.",
        )
    :   t(
            "settings.consent.document_wizard.warning_preference",
            "This preference will be visible immediately in the preference center for users on your platform.",
        );

export const getSuccessMessage = (type: ConsentCreationType, isPartial: boolean, t: TFunction) => {
    if (isPartial) {
        return t(
            "settings.consent.document_wizard.created_partial",
            "Purpose created, but some translations could not be saved.",
        );
    }

    return type === "document" ?
            t("settings.consent.document_wizard.created_document", "Contract document created successfully.")
        :   t("settings.consent.document_wizard.created_preference", "Preference created successfully.");
};

export const getErrorMessage = (type: ConsentCreationType, t: TFunction) =>
    type === "document" ?
        t("settings.consent.document_wizard.error_document", "Failed to create the contract document.")
    :   t("settings.consent.document_wizard.error_preference", "Failed to create the preference.");

export const getCompletedLocaleCodes = (state: ConsentCreationState, locales: Locale[]) =>
    locales
        .map((locale) => locale.code ?? "")
        .filter((localeCode) => {
            const labelDraft = state.labelDrafts[localeCode];
            const versionDraft = state.versionDrafts[localeCode];

            return Boolean(labelDraft?.name.trim() && versionDraft?.content.trim());
        });

export const buildStepErrors = (
    state: ConsentCreationState,
    defaultLocale: string,
    t: TFunction,
): ConsentCreationStepErrors => {
    const defaultLabelDraft = state.labelDrafts[defaultLocale] ?? { description: "", name: "" };
    const defaultVersionDraft = state.versionDrafts[defaultLocale] ?? { content: "", documentUrl: "" };

    return {
        category:
            state.category.trim().length === 0 ?
                t("settings.consent.document_wizard.validation.category", "Select a category.")
            :   "",
        code:
            state.code.trim().length === 0 ?
                t("settings.consent.document_wizard.validation.code_required", "Technical key is required.")
            : !TECHNICAL_CODE_PATTERN.test(state.code.trim()) ?
                t(
                    "settings.consent.document_wizard.validation.code_pattern",
                    "Use only lowercase letters, digits, and underscores.",
                )
            :   "",
        name:
            defaultLabelDraft.name.trim().length === 0 ?
                t("settings.consent.document_wizard.validation.name_required", "Label is required.")
            :   "",
        description: "",
        version:
            state.version.trim().length === 0 ?
                t("settings.consent.document_wizard.validation.version_required", "Version number is required.")
            :   "",
        content:
            defaultVersionDraft.content.trim().length === 0 ?
                t("settings.consent.document_wizard.validation.content_required", "Legal content is required.")
            :   "",
        documentUrl:
            (
                defaultVersionDraft.documentUrl.trim().length > 0 &&
                !/^https?:\/\/.+/i.test(defaultVersionDraft.documentUrl.trim())
            ) ?
                t("settings.consent.document_wizard.validation.document_url", "Enter a valid URL.")
            :   "",
    };
};

export const canAdvanceConsentCreationStep = (step: number, errors: ConsentCreationStepErrors) => {
    if (step === 0) {
        return !errors.category && !errors.code;
    }

    if (step === 1) {
        return !errors.name;
    }

    if (step === 2) {
        return !errors.version && !errors.content && !errors.documentUrl;
    }

    return false;
};
